import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Circle, Marker, MapContainer, TileLayer, Tooltip, useMapEvents } from "react-leaflet";

// Fix marker icon rendering in CRA builds.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_RADIUS_METERS = 1000;

function ClickHandler({ onLatLngChange }) {
  useMapEvents({
    click(e) {
      if (!onLatLngChange) return;
      onLatLngChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export default function VillageMapPickerOSM({
  latitude,
  longitude,
  onLatLngChange,
  geocodeAddress,
  villageLabel,
  autoGeocodeWhenMissing = false,
  mapHeight = "280px",
  showCoordinatePanel = true,
}) {
  const didAutoGeocodeRef = useRef(false);
  const mapInstanceRef = useRef(null);
  const [autoStatus, setAutoStatus] = useState("idle"); // idle | searching | found | not_found
  const [autoFoundLabel, setAutoFoundLabel] = useState("");
  const [autoFoundMeta, setAutoFoundMeta] = useState({ type: "", importance: "" });
  const [pinLabel, setPinLabel] = useState("");
  const reverseTimerRef = useRef(null);

  const latNum =
    latitude !== undefined && latitude !== null && latitude !== ""
      ? Number(latitude)
      : null;
  const lngNum =
    longitude !== undefined && longitude !== null && longitude !== ""
      ? Number(longitude)
      : null;

  const hasPos =
    latNum != null &&
    lngNum != null &&
    Number.isFinite(latNum) &&
    Number.isFinite(lngNum);

  const center = useMemo(
    () => (hasPos ? { lat: latNum, lng: lngNum } : DEFAULT_CENTER),
    [hasPos, latNum, lngNum]
  );

  const markerPos = hasPos ? [latNum, lngNum] : [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng];

  const mapStyle = useMemo(
    () => ({
      width: "100%",
      height: mapHeight,
      borderRadius: showCoordinatePanel ? "6px" : 0,
    }),
    [mapHeight, showCoordinatePanel]
  );

  // When this component is inside a modal, the container size can change after render.
  // Leaflet needs invalidateSize() so tiles/marker become visible.
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    try {
      mapInstanceRef.current.invalidateSize();
      if (hasPos) {
        mapInstanceRef.current.setView([latNum, lngNum], 14);
      }
    } catch (e) {
      // ignore
    }
  }, [mapHeight, hasPos, latNum, lngNum]);

  // Auto geocode (OpenStreetMap Nominatim) when coordinates are missing.
  useEffect(() => {
    if (!autoGeocodeWhenMissing) return;
    if (hasPos) return;
    if (!geocodeAddress || String(geocodeAddress).trim().length === 0) return;
    if (didAutoGeocodeRef.current) return;
    didAutoGeocodeRef.current = true;
    setAutoStatus("searching");
    setAutoFoundLabel("");
    setAutoFoundMeta({ type: "", importance: "" });

    const apiBase = process.env.REACT_APP_NODE_URL || "";
    const token = localStorage.getItem("rbacToken");

    axios
      .get(`${apiBase}/api/master/geocode`, {
        params: { address: String(geocodeAddress).trim() },
        headers: { token },
      })
      .then((resp) => {
        const lat = resp?.data?.result?.lat;
        const lng = resp?.data?.result?.lng;
        const displayName = resp?.data?.result?.displayName;
        const type = resp?.data?.result?.type;
        const importance = resp?.data?.result?.importance;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setAutoStatus("found");
          setAutoFoundLabel(displayName || "");
          // Until user clicks/drag, show the found label.
          setPinLabel(displayName || "");
          setAutoFoundMeta({
            type: type || "",
            importance:
              importance === undefined || importance === null ? "" : String(importance),
          });
          onLatLngChange(lat, lng);
        } else {
          didAutoGeocodeRef.current = false; // allow retry
          setAutoStatus("not_found");
        }
      })
      .catch((err) => {
        console.error("VillageMapPickerOSM geocode error:", err?.message || err);
        didAutoGeocodeRef.current = false;
        setAutoStatus("not_found");
      });
  }, [autoGeocodeWhenMissing, hasPos, geocodeAddress, onLatLngChange]);

  // Reset geocode attempt when inputs change.
  useEffect(() => {
    didAutoGeocodeRef.current = false;
    setAutoStatus("idle");
    setAutoFoundLabel("");
    setAutoFoundMeta({ type: "", importance: "" });
    setPinLabel("");
  }, [geocodeAddress, hasPos]);

  const requestReverseGeocode = useCallback(
    (lat, lng) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const apiBase = process.env.REACT_APP_NODE_URL || "";
      const token = localStorage.getItem("rbacToken");

      if (reverseTimerRef.current) {
        clearTimeout(reverseTimerRef.current);
      }

      reverseTimerRef.current = setTimeout(() => {
        axios
          .get(`${apiBase}/api/master/reverse-geocode`, {
            params: { lat, lng },
            headers: { token },
          })
          .then((resp) => {
            const label = resp?.data?.result;
            setPinLabel(label || "");
          })
          .catch((err) => {
            console.error("reverse-geocode error:", err?.message || err);
            setPinLabel("");
          });
      }, 400);
    },
    []
  );

  const placePin = useCallback(
    (lat, lng) => {
      if (!onLatLngChange) return;
      onLatLngChange(lat, lng);
      requestReverseGeocode(lat, lng);
    },
    [onLatLngChange, requestReverseGeocode]
  );

  return (
    <div>
      <p className="small text-muted mb-2">
        Click the map to set the village center (OpenStreetMap, no billing). Coverage radius is fixed to 1km.
      </p>
      {autoStatus === "searching" && (
        <div className="small text-muted mb-2">Searching village coordinates…</div>
      )}
      {autoStatus === "not_found" && (
        <div className="small text-danger mb-2">
          Could not auto-find exact coordinates. Please click on map.
        </div>
      )}
      {autoStatus === "found" && autoFoundLabel && (
        <div className="small text-muted mb-2">
          Found: <b>{autoFoundLabel}</b>
          {autoFoundMeta.type ? <> (type: {autoFoundMeta.type})</> : null}
        </div>
      )}

      <MapContainer
        style={mapStyle}
        center={[center.lat, center.lng]}
        zoom={hasPos ? 16 : 5}
        scrollWheelZoom={true}
        whenCreated={(map) => {
          mapInstanceRef.current = map;
        }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClickHandler onLatLngChange={placePin} />

        <Marker
          position={markerPos}
          draggable={true}
          key={
            hasPos
              ? `village-marker-${latNum}-${lngNum}`
              : "village-marker-default"
          }
          eventHandlers={{
            dragend: (e) => {
              const next = e.target.getLatLng();
              if (!onLatLngChange) return;
              onLatLngChange(next.lat, next.lng);
              requestReverseGeocode(next.lat, next.lng);
            },
          }}
        >
          <Tooltip
            key={
              hasPos
                ? `village-tooltip-${latNum}-${lngNum}-${villageLabel || ""}`
                : "village-tooltip-default"
            }
            permanent={true}
            direction="top"
            offset={[0, -18]}
            opacity={1}
          >
            <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
              {pinLabel || villageLabel || "Selected village"}
            </div>
          </Tooltip>
        </Marker>

        {hasPos && (
          <Circle
            center={[latNum, lngNum]}
            radius={DEFAULT_RADIUS_METERS}
            pathOptions={{
              fillColor: "#1976d2",
              fillOpacity: 0.15,
              color: "#1976d2",
              opacity: 0.6,
              weight: 1,
            }}
          />
        )}
      </MapContainer>

      {showCoordinatePanel && (
        <div className="row g-2 mt-2">
          <div className="col-md-6">
            <label className="form-label small mb-0">Latitude</label>
            <input
              type="text"
              className="form-control form-control-sm"
              readOnly
              value={hasPos ? String(latNum) : ""}
              placeholder="—"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label small mb-0">Longitude</label>
            <input
              type="text"
              className="form-control form-control-sm"
              readOnly
              value={hasPos ? String(lngNum) : ""}
              placeholder="—"
            />
          </div>
        </div>
      )}
    </div>
  );
}

